"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGridCoreConfigure = void 0;
const react_1 = __importStar(require("react"));
const callWidthSum_1 = require("../helpers/callWidthSum");
const totalOffsetCalculation_1 = require("../helpers/totalOffsetCalculation");
const withCalculatedColumnWidth_1 = require("../helpers/withCalculatedColumnWidth");
const defaultGridHeader_1 = require("../renderers/defaultGridHeader");
const defaultGridRow_1 = require("../renderers/defaultGridRow");
const defaultRowRenderer = (0, defaultGridRow_1.createGridRowRenderer)();
const defaultHeaderRenderer = (0, defaultGridHeader_1.createGridHeaderRenderer)();
function useGridCoreConfigure({ columns, plugins: initialPlugins, gridApi, rootRef, bodyRef, getDataByIndex, totalItemsCount, rootWidth, listBodyHeight, columnsToggleable, idGetter: initialIdGetter, headerContainerRef }) {
    const self = (0, react_1.useState)(() => ({
        tearDownCallbacks: [],
        columnsConfig: columns.map((col) => (Object.assign(Object.assign({}, col), { width: col.width || 0 })))
    }))[0];
    (0, react_1.useEffect)(() => {
        return () => {
            self.tearDownCallbacks.forEach((cb) => cb());
            self.tearDownCallbacks = [];
        };
    }, [self]);
    const plugins = (0, react_1.useMemo)(() => (initialPlugins || []).map((create) => create({
        api: gridApi,
        rootRef,
        bodyRef,
        getColumns: () => self.columnsConfig,
        getDataByIndex
    })), [bodyRef, getDataByIndex, gridApi, initialPlugins, rootRef, self.columnsConfig]);
    const rowHeightGetters = (0, react_1.useMemo)(() => plugins.map((i) => i.rowHeightGetter).filter(Boolean), [plugins]);
    const lastRowHeightGetter = (0, react_1.useMemo)(() => rowHeightGetters[rowHeightGetters.length - 1], [rowHeightGetters]);
    const rowHeight = (0, react_1.useMemo)(() => (lastRowHeightGetter ? lastRowHeightGetter(0) : 0), [lastRowHeightGetter]);
    const hasScroll = (0, react_1.useMemo)(() => (listBodyHeight ? listBodyHeight < totalItemsCount * rowHeight : false), [listBodyHeight, totalItemsCount, rowHeight]);
    const { headerOffset, bodyOffset, availableBodyWidth } = (0, totalOffsetCalculation_1.totalOffsetCalculation)(rootWidth, hasScroll, columnsToggleable);
    self.tearDownCallbacks.forEach((cb) => cb());
    self.tearDownCallbacks = [];
    plugins.forEach((plugin) => {
        if (plugin.tearDown) {
            self.tearDownCallbacks.push(plugin.tearDown);
        }
    });
    const processedColumns = (0, react_1.useMemo)(() => plugins
        .map((i) => i.processColumns)
        .reduce((acc, curr) => {
        if (curr) {
            return curr(acc);
        }
        return acc;
    }, columns)
        .map((i, idx) => { var _a; return (Object.assign(Object.assign({}, i), { order: (_a = i.order) !== null && _a !== void 0 ? _a : idx })); })
        .sort((a, b) => a.order - b.order), [columns, plugins]);
    self.columnsConfig = (0, withCalculatedColumnWidth_1.withCalculatedColumnWidth)(processedColumns, availableBodyWidth);
    const columnsWidth = (0, react_1.useMemo)(() => self.columnsConfig.reduce(callWidthSum_1.callWidthSum, 0), [self.columnsConfig]);
    const rowWidth = (0, react_1.useMemo)(() => (columnsWidth > availableBodyWidth ? columnsWidth + bodyOffset : availableBodyWidth), [availableBodyWidth, bodyOffset, columnsWidth]);
    const idGetter = (0, react_1.useCallback)((index) => {
        const data = getDataByIndex(index);
        return data ? initialIdGetter(data) : null;
    }, [getDataByIndex, initialIdGetter]);
    const rowRenderer = (0, react_1.useMemo)(() => plugins
        .map((i) => i.rowRenderer)
        .filter(Boolean)
        .reduce((acc, curr) => {
        return function rowRendererDecorated({ index, style, height, width, topOffset, handlers, disabled, inactive, selected, isOddRow, className = '' }) {
            return curr({
                getDataByIndex,
                columns: self.columnsConfig,
                idGetter: initialIdGetter,
                originalRenderer: acc,
                disabled,
                inactive,
                selected,
                isOddRow,
                handlers,
                index,
                height,
                width,
                topOffset,
                style,
                className
            });
        };
    }, defaultRowRenderer), [getDataByIndex, initialIdGetter, plugins, self.columnsConfig]);
    const headerRenderer = (0, react_1.useMemo)(() => plugins
        .map((i) => i.headerRenderer)
        .filter(Boolean)
        .reduce((acc, curr) => {
        return function headerRendererDecorated({ columns, containerRef, rowWidth, style, className }) {
            return curr({
                columns,
                containerRef,
                rowWidth,
                style,
                className,
                originalRenderer: acc
            });
        };
    }, defaultHeaderRenderer), [plugins]);
    const containerAddon = (0, react_1.useMemo)(() => react_1.default.createElement(react_1.default.Fragment, { key: 'containerAddon' }, plugins
        .filter((i) => i.containerAddons)
        .reduce((acc, curr) => {
        if (curr.containerAddons) {
            return typeof curr.containerAddons === 'function'
                ? curr.containerAddons(acc)
                : [...acc, ...curr.containerAddons];
        }
        return acc;
    }, [])
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((i) => {
        return react_1.default.createElement(react_1.default.Fragment, { key: i.id || Math.random().toString(16) }, i.render());
    })), [plugins]);
    const containerAddonRenderer = (0, react_1.useCallback)(() => {
        return containerAddon;
    }, [containerAddon]);
    const bodyAddon = (0, react_1.useMemo)(() => react_1.default.createElement(react_1.default.Fragment, { key: 'bodyAddon' }, plugins
        .filter((i) => i.bodyAddons)
        .reduce((acc, curr) => {
        if (curr.bodyAddons) {
            return typeof curr.bodyAddons === 'function'
                ? curr.bodyAddons(acc)
                : [...acc, ...curr.bodyAddons];
        }
        return acc;
    }, [])
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((i) => {
        return react_1.default.createElement(react_1.default.Fragment, { key: i.id || Math.random().toString(16) }, i.render());
    })), [plugins]);
    const bodyAddonRenderer = (0, react_1.useCallback)(() => {
        return bodyAddon;
    }, [bodyAddon]);
    const subheaderAddon = (0, react_1.useMemo)(() => react_1.default.createElement(react_1.default.Fragment, { key: 'subheaderAddon' }, plugins
        .filter((i) => i.subheaderAddons)
        .reduce((acc, curr) => {
        if (curr.subheaderAddons) {
            return typeof curr.subheaderAddons === 'function'
                ? curr.subheaderAddons(acc)
                : [...acc, ...curr.subheaderAddons];
        }
        return acc;
    }, [])
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((i) => {
        return react_1.default.createElement(react_1.default.Fragment, { key: i.id || Math.random().toString(16) }, i.render());
    })), [plugins]);
    const subheaderAddonRenderer = (0, react_1.useCallback)(() => {
        return subheaderAddon;
    }, [subheaderAddon]);
    const rootClassNames = (0, react_1.useMemo)(() => plugins.reduce((acc, curr) => {
        if (curr.rootClassNames) {
            return acc.concat(curr.rootClassNames);
        }
        return acc;
    }, []), [plugins]);
    if (!lastRowHeightGetter) {
        throw new Error('GridCore: you must define rowHeightGetter in plugins');
    }
    const hasColumns = (0, react_1.useMemo)(() => Boolean(self.columnsConfig.filter((i) => !i.isService).length), [self.columnsConfig]);
    const header = (0, react_1.useMemo)(() => headerRenderer({
        columns: self.columnsConfig,
        containerRef: headerContainerRef,
        rowWidth: rowWidth + headerOffset,
        className: '',
        style: {}
    }), [headerContainerRef, headerOffset, headerRenderer, rowWidth, self.columnsConfig]);
    return (0, react_1.useMemo)(() => {
        return {
            idGetter,
            hasColumns,
            rowRenderer,
            header,
            rowHeightGetter: lastRowHeightGetter,
            rowWidth,
            columnsWidth,
            containerAddonRenderer,
            bodyAddonRenderer,
            subheaderAddonRenderer,
            rootClassNames
        };
    }, [
        bodyAddonRenderer,
        columnsWidth,
        containerAddonRenderer,
        hasColumns,
        header,
        idGetter,
        lastRowHeightGetter,
        rootClassNames,
        rowRenderer,
        rowWidth,
        subheaderAddonRenderer
    ]);
}
exports.useGridCoreConfigure = useGridCoreConfigure;
//# sourceMappingURL=useGridCoreConfigure.js.map